import {
  DEFAULT_APPEARANCE,
  PLAYER_COMMAND_SECONDS, PlayerStance, PlayerGait, PlayerMotionMode, PlayerAction,
  type PlayerControlIntent,
  defaultWorldEnvironment, sampleWeather, sampleWorldTime, sampleWorldCalendar, sampleAirTemperature, equipmentVisualState,
  type WorldEnvironmentState,
  WaterLocomotion, sampleWorldWaterAt,
  type PlayerLocomotionOptions,
  PUBLIC_WORLD_LIGHTS,
  WORLD_REVIEW_DESTINATIONS,
  clampWorldPosition,
  getWorldHeightAt,
  getLargeWorldCollisionCandidatesAt,
  resolveLargeWorldCollision,
  type CollisionProbe,
  type DeveloperSpeed,
  type DeveloperState,
  type DeveloperTeleportTargetId,
  type StaticCollider,
} from "@endclock/shared";
import { GuestActivity } from "../guest/activity.js";
import { observeGameViewport } from "./viewport.js";
import { PlayerNameplates } from "../nameplates/nameplates.js";
import { NpcWorld } from "../npc/npc-world.js";
import { AIWorld, type AILoadState } from "../ai/ai.js";
import { DroneWorld } from "../drone/drone-world.js";
import { DroneDebugPanel } from "../drone/drone-panel.js";
import { CombatWorld } from "../combat/combat-world.js";
import { PlayerConditionStore } from "../condition/player-condition-store.js";
import { BodyConditionUi } from "../condition/body-condition-ui.js";
import { CharacterWorld, type CharacterSystemSnapshot } from "../character/character.js";
import { DEFAULT_ATMOSPHERE_FLAGS, type AtmosphereFeatureFlags } from "../engine/atmosphere.js";
import { CAMERA_CONFIG, TPS_CAMERA_CONFIG, Camera, type CameraMode } from "../engine/camera.js";
import { createCameraCollisionProbe } from "../engine/camera-collision.js";
import { ActorContactShadows } from "../engine/actor-contact-shadows.js";
import { TpsMovementController } from "../engine/tps-movement.js";
import { Renderer, type RendererMetrics } from "../engine/renderer.js";
import { buildEnvironmentPresentation, solarLighting } from "../engine/environment-presentation.js";
import { WeatherRenderer } from "../environment/weather-renderer.js";
import { GeologyWorld, type GeologyLoadState } from "../geology/geology.js";
import { HumanWorld, type HumanLoadState } from "../human/human.js";
import { ClientInventoryStore } from "../items/inventory-store.js";
import { WorldDropWorld, type WorldDropWorldSnapshot } from "../items/world-drop-world.js";
import {
  InteractionController,
  type InteractionSnapshot,
} from "../interaction/interaction-controller.js";
import type { InteractionCandidate } from "../interaction/target-selection.js";
import { HeightfieldTerrain } from "../terrain/terrain.js";
import { VegetationWorld, type VegetationLoadState } from "../vegetation/vegetation.js";
import { WorldObjectStreamer, type WorldObjectStreamingSnapshot } from "../world/world-streamer.js";
import { ClientWorldOverrideCache, type WorldOverrideCacheSnapshot } from "../world/world-overrides.js";
import { queryGameplayAim, type GameplayAim } from "./aim-query.js";
import { createWorldAimProbe } from "./world-aim-probe.js";
import { PlayerCommandSampler } from "./player-command-sampler.js";
import { PlayerPrediction } from "./player-prediction.js";
import { WaterCamera } from "../engine/water-camera.js";
import { withRenderImpulse } from "./render-impulse.js";
import { GameInput } from "./input.js";
import { NetworkClient, resolvePublicWebSocketOverride, type ConnectionState } from "./network.js";

export type GraphicsQuality = "auto" | "low" | "medium" | "high";
export type FrameRate = "auto" | "30" | "60";

export interface GameSettings {
  graphics: GraphicsQuality;
  frameRate: FrameRate;
}

export interface DeveloperRenderFlags {
  terrain: boolean;
  water: boolean;
  geology: boolean;
  vegetation: boolean;
  human: boolean;
  ai: boolean;
  characters: boolean;
  particles: boolean;
}

const DEFAULT_DEVELOPER_RENDER_FLAGS: DeveloperRenderFlags = Object.freeze({
  terrain: true,
  water: true,
  geology: true,
  vegetation: true,
  human: true,
  ai: true,
  characters: true,
  particles: true,
});

interface QualityPreset {
  readonly scale: number;
  readonly maximumWidth: number;
}

const presets: Readonly<Record<Exclude<GraphicsQuality, "auto">, QualityPreset>> = {
  low: { scale: 0.9, maximumWidth: 760 },
  medium: { scale: 1, maximumWidth: 1_024 },
  high: { scale: 1.15, maximumWidth: 1_280 },
};

export class EndclockGame {
  readonly renderer: Renderer;
  readonly camera = new Camera();
  private readonly tpsMovement = new TpsMovementController();
  readonly network: NetworkClient;
  readonly input: GameInput;
  readonly terrain: HeightfieldTerrain;
  private readonly contactShadows: ActorContactShadows;
  private readonly weatherEffects: WeatherRenderer;
  private environmentState: WorldEnvironmentState = defaultWorldEnvironment(Date.now());
  private environmentServerAt = Date.now();
  private environmentReceivedAt = performance.now();
  private environmentUpdateCpuMs = 0;
  private environmentFlash = 0;
  readonly geology: GeologyWorld;
  readonly vegetation: VegetationWorld;
  readonly human: HumanWorld;
  readonly ai: AIWorld;
  readonly drones: DroneWorld;
  readonly dronePanel: DroneDebugPanel;
  readonly combat: CombatWorld;
  readonly condition = new PlayerConditionStore();
  readonly bodyUi: BodyConditionUi;
  private readonly combatImpulse: [number, number, number] = [0, 0, 0];
  readonly npcs: NpcWorld;
  readonly nameplates: PlayerNameplates;
  readonly guestActivity: GuestActivity;
  private readonly canvas: HTMLCanvasElement;
  private readonly disposeViewport: () => void;
  readonly character: CharacterWorld;
  readonly world: WorldObjectStreamer;
  readonly worldOverrides: ClientWorldOverrideCache;
  readonly interaction: InteractionController;
  readonly inventory: ClientInventoryStore;
  readonly worldDrops: WorldDropWorld;
  settings: GameSettings;
  onConnectionState: ((state: ConnectionState) => void) | undefined;
  onCameraModeChange: ((mode: CameraMode) => void) | undefined;
  onDeveloperState: NetworkClient["onDeveloperState"];
  onInteractionState: ((snapshot: InteractionSnapshot) => void) | undefined;
  private readonly prediction = new PlayerPrediction(0, 12);
  private readonly waterCamera = new WaterCamera();
  private get playerX(): number { return this.prediction.state.x; }
  private set playerX(value: number) { this.prediction.state.x = value; }
  private get playerY(): number { return this.prediction.state.y; }
  private set playerY(value: number) { this.prediction.state.y = value; }
  private get playerZ(): number { return this.prediction.state.z; }
  private set playerZ(value: number) { this.prediction.state.z = value; }
  private get velocityX(): number { return this.prediction.state.velocityX; }
  private get velocityY(): number { return this.prediction.state.velocityY; }
  private get velocityZ(): number { return this.prediction.state.velocityZ; }
  private previousFrame = 0;
  private animationFrame = 0;
  private networkAccumulator = 0;
  private pendingHandActions = 0;
  private readonly commandSampler = new PlayerCommandSampler();
  private aimQuery: GameplayAim | undefined;
  private cameraCpuMs = 0;
  private aimQueryCpuMs = 0;
  private readonly aimProbe = createWorldAimProbe((x, z) => this.terrain.getHeightAt(x, z));
  private quality: QualityPreset = presets.medium;
  private resolvedQuality: Exclude<GraphicsQuality, "auto"> = "medium";
  private renderedFrames = 0;
  private performanceWindow = 0;
  private contextLost = false;
  private readonly collisionProbe: CollisionProbe = { candidateCount: 0 };
  private collisionSamples = 0;
  private collisionCandidateTotal = 0;
  private authoritativeX = 0;
  private authoritativeY = getWorldHeightAt(0, 12);
  private authoritativeZ = 12;
  private freeCamEnabled = false;
  private readonly freeCamTarget: [number, number, number] = [0, getWorldHeightAt(0, 12), 12];
  private developerRenderFlags: DeveloperRenderFlags = { ...DEFAULT_DEVELOPER_RENDER_FLAGS };
  private readonly defaultAtmosphereFlags: AtmosphereFeatureFlags;
  private readonly defaultAiGlowEnabled: boolean;
  private readonly interactionCandidateScratch: InteractionCandidate[] = [];
  private cameraColliders: readonly StaticCollider[] = [];
  private cameraColliderX = Infinity;
  private cameraColliderZ = Infinity;

  constructor(
    canvas: HTMLCanvasElement,
    joystick: HTMLElement,
    joystickKnob: HTMLElement,
    settings: GameSettings,
    atmosphereFlags: AtmosphereFeatureFlags = DEFAULT_ATMOSPHERE_FLAGS,
    aiGlowEnabled = true,
    interactionButton?: HTMLElement,
  ) {
    this.canvas = canvas;
    this.defaultAtmosphereFlags = { ...atmosphereFlags };
    this.defaultAiGlowEnabled = aiGlowEnabled;
    this.settings = settings;
    this.renderer = new Renderer(canvas, { responsiveResolution: true, advancedLighting: true });
    this.weatherEffects = new WeatherRenderer(this.renderer.gl);
    this.renderer.setAtmosphereFlags(atmosphereFlags);
    this.renderer.setAiGlowEnabled(aiGlowEnabled);
    this.renderer.setLocalLights(PUBLIC_WORLD_LIGHTS);
    this.input = new GameInput(canvas, joystick, joystickKnob, interactionButton);
    this.terrain = new HeightfieldTerrain(this.renderer);
    this.contactShadows = new ActorContactShadows(this.renderer, (x, z) => this.terrain.getHeightAt(x, z));
    this.camera.collisionProbe = createCameraCollisionProbe(
      (x, z) => this.terrain.getHeightAt(x, z),
      () => this.cameraColliders,
    );
    this.geology = new GeologyWorld(this.renderer);
    this.vegetation = new VegetationWorld(this.renderer);
    this.human = new HumanWorld(this.renderer);
    this.ai = new AIWorld(this.renderer);
    this.character = new CharacterWorld(this.renderer, this.terrain, atmosphereFlags);
    this.worldOverrides = new ClientWorldOverrideCache();
    this.world = new WorldObjectStreamer(
      this.terrain,
      this.geology,
      this.vegetation,
      this.human,
      this.ai,
      this.worldOverrides,
    );
    const configuredWs = import.meta.env.VITE_GAME_WS_URL as string | undefined;
    const wsOverride = resolvePublicWebSocketOverride(
      new URLSearchParams(location.search).get("ws"),
      configuredWs,
      location,
    );
    this.network = new NetworkClient(DEFAULT_APPEARANCE, wsOverride);
    const droneParent = canvas.parentElement ?? document.body;
    this.npcs = new NpcWorld(this.renderer, droneParent, this.network, atmosphereFlags);
    this.nameplates = new PlayerNameplates(droneParent);
    this.guestActivity = new GuestActivity(droneParent, (kind) => this.network.reportActivity(kind));
    this.network.onGuestStatus = (message) => this.guestActivity.receive(message);
    this.network.onEnvironment = (message) => {
      this.environmentState = message.state;
      this.environmentServerAt = message.serverTime;
      this.environmentReceivedAt = performance.now();
    };
    this.network.onLightning = (event) => this.weatherEffects.lightning.ingest(event, this.environmentNow());
    this.network.onNpcSnapshot = (npcs, at, tick) => this.npcs.ingest(npcs, at, tick);
    this.network.onNpcMessage = (message) => this.npcs.panel.handle(message);
    this.network.onNpcReset = () => this.npcs.reset();
    this.drones = new DroneWorld(this.renderer, this.ai.library, droneParent);
    this.dronePanel = new DroneDebugPanel(droneParent, this.network);
    this.combat = new CombatWorld(this.renderer, (x, z) => this.terrain.getHeightAt(x, z));
    this.bodyUi = new BodyConditionUi(this.condition, {
      setModal: open => this.input.setUiBlocked("body", open),
      sendDeveloper: command => this.network.developerGameplayCommand(command),
      isDeveloperAuthorized: () => this.network.developer.authorized,
    });
    this.network.onCondition = message => this.condition.ingest(message);
    this.network.onCombat = message => this.combat.ingest(message, message.at);
    this.network.onCombatEvents = message => this.combat.ingestEvents(message.events, message.at);
    this.network.onDroneSnapshot = (drones, at) => {
      this.drones.ingest(drones, at);
      if (this.dronePanel.selectedId && !this.drones.stateFor(this.dronePanel.selectedId)) {
        this.dronePanel.select(null);
        this.dronePanel.reset();
      }
    };
    this.network.onDroneTelemetry = (message) => this.drones.setTelemetry(message);
    this.network.onDroneShoutResult = (message) => this.dronePanel.handleShoutResult(message);
    this.network.onDroneReset = () => { this.drones.reset(); this.dronePanel.reset(); };
    this.input.onCanvasTap = (x, y) => {
      if (this.npcs.pick(x, y, canvas, this.camera, (worldX, worldZ) => this.terrain.getHeightAt(worldX, worldZ))) return;
      const id = this.drones.pick(x, y, canvas, this.camera, (worldX, worldZ) => this.terrain.getHeightAt(worldX, worldZ));
      if (id) this.dronePanel.select(id);
    };
    this.inventory = new ClientInventoryStore({
      onResyncRequired: (request) => {
        if (request.kind === "INVENTORY") this.network.requestInventoryResync();
        else this.network.requestContainerResync(request.accessSessionId);
      },
    });
    this.worldDrops = new WorldDropWorld(this.renderer);
    this.inventory.subscribe((snapshot) => {
      this.worldDrops.setSnapshot(snapshot);
      this.character.setLocalEquipmentVisual(snapshot.physical ? equipmentVisualState(snapshot.physical) : { garments: {}, mainHand: null });
    });
    this.network.onEquipment = (entries) => this.character.setRemoteEquipmentVisuals(entries);
    this.interaction = new InteractionController(this.network);
    this.network.onStateChange = (state) => this.onConnectionState?.(state);
    this.network.onSessionReset = () => {
      this.condition.reset(); this.combat.reset();
      this.commandSampler.cancel(); this.networkAccumulator = 0; this.pendingHandActions = 0; this.input.reset();
      this.renderer.clearWaterEffects();
      this.interaction.reset();
      this.inventory.reset();
      if (this.worldOverrides.clear()) this.world.refreshWorldOverrides();
    };
    this.network.onInteractionStarted = (message) => this.interaction.handleStarted(message);
    this.network.onInteractionCompleted = (message) => this.interaction.handleCompleted(message);
    this.network.onInteractionCancelled = (message) => this.interaction.handleCancelled(message);
    this.network.onInteractionRejected = (message) => this.interaction.handleRejected(message);
    this.network.onWorldOverrideSync = (message) => {
      if (this.worldOverrides.hydrate(message.ownerChunks, message.overrides).length > 0) {
        this.world.refreshWorldOverrides();
      }
    };
    this.network.onWorldOverrideDelta = (message) => {
      if (this.worldOverrides.apply(message.override)) this.world.refreshWorldOverrides();
    };
    this.network.onInventoryMessage = (message) => this.inventory.handle(message);
    this.network.onDeveloperState = (state, teleport) => {
      this.bodyUi.refreshDeveloperAccess();
      if (teleport) {
        this.prediction.teleport(teleport.x, teleport.y, teleport.z);
        this.commandSampler.cancel(); this.networkAccumulator = 0; this.pendingHandActions = 0; this.input.reset();
        this.renderer.clearWaterEffects();
        if (this.camera.mode === "tps") {
          this.tpsMovement.reset();
          this.camera.resetFollow();
          this.camera.update(this.playerX, this.playerY, this.playerZ, this.renderer.aspect, 0, this.character.facingYaw);
        }
      }
      this.onDeveloperState?.(state, teleport);
    };
    this.applySettings(settings);

    canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      this.contextLost = true;
      document.body.classList.add("context-lost");
    });
    canvas.addEventListener("webglcontextrestored", () => location.reload());
    document.addEventListener("visibilitychange", () => {
      this.previousFrame = performance.now();
      this.performanceWindow = 0;
      this.renderedFrames = 0;
      this.renderer.resetFrameTiming();
      if (document.hidden) this.input.reset();
    });
    this.disposeViewport = observeGameViewport(canvas, (width, height) => {
      this.renderer.resize(width, height);
      this.input.viewportChanged();
      if (canvas.parentElement) canvas.parentElement.dataset.orientation = width < height ? "portrait" : "landscape";
    });
  }

  start(): void {
    this.previousFrame = performance.now();
    this.network.connect();
    this.camera.update(this.playerX, this.terrain.getHeightAt(this.playerX, this.playerZ), this.playerZ, this.renderer.aspect, 1);
    this.animationFrame = requestAnimationFrame((time) => this.frame(time));
  }

  stop(): void {
    cancelAnimationFrame(this.animationFrame);
    this.disposeViewport();
    this.input.destroy();
    this.network.disconnect();
    this.worldDrops.dispose();
    this.npcs.dispose();
    this.nameplates.dispose();
    this.guestActivity.dispose();
    this.drones.dispose();
    this.dronePanel.dispose();
    this.combat.dispose(); this.bodyUi.dispose();
    this.terrain.dispose();
    this.contactShadows.dispose();
    this.weatherEffects.dispose();
    this.renderer.disposeLighting();
    this.renderer.disposeWater();
  }

  enterWorld(): void {
    this.network.connect();
  }

  setCameraMode(mode: CameraMode): void {
    this.freeCamEnabled = false;
    this.tpsMovement.reset();
    this.commandSampler.cancel();
    this.camera.setMode(mode);
    this.input.setCameraMode(mode);
    this.camera.update(this.playerX, this.playerY, this.playerZ, this.renderer.aspect, 0, this.character.facingYaw);
    this.renderer.setTpsAtmosphere(mode === "tps");
    this.onCameraModeChange?.(mode);
  }

  profileAt(x: number, z: number, zoom?: number): void {
    if (!this.network.developer.authorized) throw new Error("Server-authorized developer access is required");
    const destination = Object.values(WORLD_REVIEW_DESTINATIONS).find(
      (candidate) => Math.hypot(candidate.x - x, candidate.z - z) <= 1,
    );
    if (!destination) throw new Error("Arbitrary client-side teleport is disabled");
    this.teleportDeveloper(destination.id, zoom);
  }

  setAiGlowEnabled(enabled: boolean): void {
    this.renderer.setAiGlowEnabled(enabled);
  }

  setCharacterBenchmark(count: number): void {
    this.character.setBenchmarkCharacterCount(count);
  }

  authorizeDeveloper(token: string): void {
    this.network.authorizeDeveloper(token);
  }

  observeBattle(actorId?: number, side = false, orbit?: number): void {
    if (!this.network.developer.authorized) return;
    const actor = actorId === undefined ? undefined : this.combat.snapshot().actors.find(value => value.id === actorId);
    if (actor) {
      const target = actor.renderPosition, angle = target.yaw + (Number.isFinite(orbit) ? orbit! : side ? Math.PI / 2 : Math.PI * .78);
      const distance = actor.type === 0 ? 3 : side ? actor.geometry.width / 2 + 1.3 : 7;
      const x = target.x + Math.sin(angle) * distance, z = target.z + Math.cos(angle) * distance;
      this.network.developerGameplayCommand({ t: "dev_cmd", command: "teleport", x, z });
      this.camera.setAim(Math.atan2(target.x - x, target.z - z), -.02);
    } else {
      this.network.developerGameplayCommand({ t: "dev_cmd", command: "teleport", x: -115, z: 73 });
      this.camera.setAim(0, -0.035);
    }
  }

  lookAtBattleActor(actorId: number): void {
    if (!this.network.developer.authorized) return;
    const actor = this.combat.snapshot().actors.find(value => value.id === actorId);
    if (!actor) return;
    const target = actor.renderPosition;
    this.camera.setAim(Math.atan2(target.x - this.playerX, target.z - this.playerZ), -.02);
  }

  setDeveloperNoclip(enabled: boolean): void {
    this.network.setDeveloperNoclip(enabled);
  }

  setDeveloperFreeFly(enabled: boolean): void {
    this.network.setDeveloperFreeFly(enabled);
  }

  setDeveloperSpeed(speed: DeveloperSpeed): void {
    this.network.setDeveloperSpeed(speed);
  }

  teleportDeveloper(target: DeveloperTeleportTargetId, zoom?: number): void {
    if (!this.network.developer.authorized) return;
    if (zoom !== undefined) this.camera.zoom = Math.max(18, Math.min(144, zoom));
    this.network.teleportDeveloper(target);
  }

  teleportDeveloperCoordinates(x: number, z: number, y?: number): void {
    this.network.teleportDeveloperCoordinates(x, z, y);
  }

  setDeveloperFreeCam(enabled: boolean): void {
    if (!this.network.developer.authorized) return;
    if (enabled) this.setCameraMode("legacy");
    this.freeCamEnabled = enabled;
    if (enabled) {
      this.freeCamTarget[0] = this.playerX;
      this.freeCamTarget[1] = this.playerY;
      this.freeCamTarget[2] = this.playerZ;
    } else {
      this.resetCamera();
    }
  }

  teleportPlayerToCamera(): void {
    if (!this.freeCamEnabled) return;
    this.teleportDeveloperCoordinates(this.freeCamTarget[0], this.freeCamTarget[2], this.freeCamTarget[1]);
  }

  resetCamera(): void {
    this.freeCamEnabled = false;
    this.tpsMovement.reset();
    this.camera.reset();
    this.camera.update(this.playerX, this.playerY, this.playerZ, this.renderer.aspect, 0, this.character.facingYaw);
  }

  setDeveloperRenderFlag<K extends keyof DeveloperRenderFlags>(flag: K, enabled: DeveloperRenderFlags[K]): void {
    if (!this.network.developer.authorized) return;
    this.developerRenderFlags = { ...this.developerRenderFlags, [flag]: enabled };
    this.renderer.invalidateLocalShadowCache();
  }

  setDeveloperAtmosphereFlag(flag: keyof AtmosphereFeatureFlags, enabled: boolean): void {
    if (!this.network.developer.authorized) return;
    const current = this.renderer.atmosphereSnapshot().flags;
    this.renderer.setAtmosphereFlags({ ...current, [flag]: enabled });
  }

  resetDeveloperLocalState(): void {
    this.resetCamera();
    this.developerRenderFlags = { ...DEFAULT_DEVELOPER_RENDER_FLAGS };
    this.renderer.invalidateLocalShadowCache();
    this.renderer.setAtmosphereFlags(this.defaultAtmosphereFlags);
    this.renderer.setAiGlowEnabled(this.defaultAiGlowEnabled);
    this.input.setDeveloperVertical(0);
  }

  applySettings(settings: GameSettings): void {
    this.settings = settings;
    const quality = settings.graphics === "auto" ? chooseAutomaticPreset() : settings.graphics;
    this.resolvedQuality = quality;
    this.quality = presets[quality];
    this.renderer.setQuality(this.quality.scale, this.quality.maximumWidth, quality);
  }

  snapshot(): {
    combat: ReturnType<CombatWorld["snapshot"]> & { bytesReceived: number };
    condition: PlayerConditionStore["snapshot"];
    mode: "game";
    control: ReturnType<EndclockGame["characterControl"]> & { pointerLock: string; cameraCpuMs: number; aimQueryCpuMs: number;
      aimPoint: GameplayAim | null; savedCommands: number; hardCorrections: number; reconciliations: number;
      visualCorrectionMagnitude: number; renderedPosition: number[] };
    player: {
      x: number; y: number; z: number; groundHeight: number;
      velocityX: number; velocityY: number; velocityZ: number; speed: number;
      authoritativeX: number; authoritativeY: number; authoritativeZ: number;
      reconciliationError: number; reconciliationCorrections: number;
      locomotion: WaterLocomotion; wetness: number; authoritativeLocomotion: WaterLocomotion;
      water: ReturnType<typeof sampleWorldWaterAt>;
    };
    camera: { underwaterAmount: number; mode: CameraMode; yaw: number; orbitYaw: number; pitch: number; zoom: number; distance: number; position: { x: number; y: number; z: number }; target: { x: number; y: number; z: number }; spanWidth: number; spanHeight: number; viewProjection: number[] };
    viewport: { width: number; height: number; aspect: number; orientation: "portrait" | "landscape" };
    metrics: RendererMetrics;
    network: {
      state: ConnectionState; id: string; generation: number; tick: number; ping: number;
      sent: number; received: number; buffered: number; reconnects: number; snapshotAge: number;
      latestInput: number; acknowledgedInput: number; pendingInputs: number; developer: DeveloperState;
    };
    geology: { state: GeologyLoadState; error?: string };
    vegetation: { state: VegetationLoadState; error?: string };
    human: { state: HumanLoadState; error?: string };
    ai: { state: AILoadState; error?: string; glowEnabled: boolean };
    character: CharacterSystemSnapshot;
    atmosphere: ReturnType<Renderer["atmosphereSnapshot"]> & { quality: Exclude<GraphicsQuality, "auto"> };
    lighting: ReturnType<Renderer["lightingSnapshot"]>;
    environment: {
      state: WorldEnvironmentState; worldTimeMinutes: number; weather: ReturnType<typeof sampleWeather>;
      dayOfYear: number; airTemperatureC: number;
      lightningFlash: number; lightningReceived: number; updateCpuMs: number; effects: WeatherRenderer["metrics"];
    };
    water: ReturnType<Renderer["waterSnapshot"]>;
    streaming: ReturnType<HeightfieldTerrain["snapshot"]>;
    world: WorldObjectStreamingSnapshot;
    interaction: InteractionSnapshot;
    worldOverrides: WorldOverrideCacheSnapshot;
    inventory: {
      revision: number;
      massGrams: number;
      slotCount: number;
      usedSlots: number;
      containerRevision: number;
      containerSessionId: string | null;
      pendingRequests: number;
      lastResult: string | null;
    };
    worldDrops: WorldDropWorldSnapshot;
    drone: ReturnType<DroneWorld["snapshot"]> & { selectedId: string | null };
    npc: ReturnType<NpcWorld["snapshot"]>;
    meshBytes: number;
    dev: { freeCam: boolean; freeCamTarget: { x: number; y: number; z: number }; inputVertical: -1 | 0 | 1; render: DeveloperRenderFlags };
  } {
    const groundHeight = this.terrain.getHeightAt(this.playerX, this.playerZ);
    return {
      mode: "game",
      control: { ...this.characterControl(), pointerLock: this.input.pointerLockState, cameraCpuMs: this.cameraCpuMs,
        aimQueryCpuMs: this.aimQueryCpuMs, aimPoint: this.aimQuery ?? null, savedCommands: this.prediction.pendingCount,
        hardCorrections: this.prediction.corrections, reconciliations: this.prediction.reconciliations,
        visualCorrectionMagnitude: Math.hypot(this.prediction.renderOffset.x, this.prediction.renderOffset.y, this.prediction.renderOffset.z),
        renderedPosition: [this.prediction.renderX, this.prediction.renderY, this.prediction.renderZ] },
      environment: {
        state: this.environmentState, worldTimeMinutes: sampleWorldTime(this.environmentState, this.environmentNow()),
        dayOfYear: sampleWorldCalendar(this.environmentState, this.environmentNow()).dayOfYear,
        airTemperatureC: sampleAirTemperature(this.environmentState, this.environmentNow()),
        weather: sampleWeather(this.environmentState, this.environmentNow()), lightningFlash: this.environmentFlash,
        lightningReceived: this.weatherEffects.lightning.received, updateCpuMs: this.environmentUpdateCpuMs,
        effects: { ...this.weatherEffects.metrics },
      },
      player: {
        x: this.playerX,
        y: this.playerY,
        z: this.playerZ,
        groundHeight,
        velocityX: this.velocityX,
        velocityY: this.velocityY,
        velocityZ: this.velocityZ,
        speed: Math.hypot(this.velocityX, this.velocityY, this.velocityZ),
        authoritativeX: this.authoritativeX,
        authoritativeY: this.authoritativeY,
        authoritativeZ: this.authoritativeZ,
        reconciliationError: this.prediction.error,
        reconciliationCorrections: this.prediction.corrections,
        locomotion: this.prediction.state.locomotion,
        wetness: this.prediction.state.wetness,
        authoritativeLocomotion: this.network.latest.self?.[6] ?? WaterLocomotion.Ground,
        water: sampleWorldWaterAt(this.playerX, this.playerZ),
      },
      camera: {
        underwaterAmount: this.waterCamera.underwaterAmount,
        mode: this.camera.mode,
        distance: this.camera.distance,
        position: { x: this.camera.position[0], y: this.camera.position[1], z: this.camera.position[2] },
        yaw: this.camera.yaw,
        orbitYaw: this.camera.orbitYaw,
        pitch: this.camera.pitch,
        zoom: this.camera.zoom,
        target: { x: this.camera.target[0], y: this.camera.target[1], z: this.camera.target[2] },
        spanWidth: 2 / this.camera.projection[0]!,
        spanHeight: 2 / this.camera.projection[5]!,
        viewProjection: Array.from(this.camera.viewProjection),
      },
      viewport: {
        width: this.renderer.viewportWidth,
        height: this.renderer.viewportHeight,
        aspect: this.renderer.aspect,
        orientation: this.renderer.aspect < 1 ? "portrait" : "landscape",
      },
      metrics: { ...this.renderer.metrics },
      lighting: this.renderer.lightingSnapshot(),
      water: this.renderer.waterSnapshot(),
      network: {
        state: this.network.state,
        id: this.network.sessionId,
        generation: this.network.latest.generation,
        tick: this.network.latest.tick,
        ping: this.network.ping,
        sent: this.network.bytesSent,
        received: this.network.bytesReceived,
        buffered: this.network.bufferedAmount,
        reconnects: this.network.reconnectCount,
        snapshotAge: this.network.latest.receivedAt > 0 ? performance.now() - this.network.latest.receivedAt : 0,
        latestInput: this.network.latestInputSequence,
        acknowledgedInput: this.network.acknowledgedInputSequence,
        pendingInputs: this.network.pendingInputCount,
        developer: this.network.developer,
      },
      geology: {
        state: this.geology.state,
        ...(this.geology.loadError ? { error: this.geology.loadError } : {}),
      },
      vegetation: {
        state: this.vegetation.state,
        ...(this.vegetation.loadError ? { error: this.vegetation.loadError } : {}),
      },
      human: {
        state: this.human.state,
        ...(this.human.loadError ? { error: this.human.loadError } : {}),
      },
      ai: {
        state: this.ai.state,
        ...(this.ai.loadError ? { error: this.ai.loadError } : {}),
        glowEnabled: this.renderer.aiGlowSnapshot().enabled,
      },
      character: this.character.snapshot(),
      atmosphere: { ...this.renderer.atmosphereSnapshot(), quality: this.resolvedQuality },
      streaming: this.terrain.snapshot(),
      world: this.world.snapshot(),
      interaction: this.interaction.snapshot(),
      worldOverrides: this.worldOverrides.snapshot(),
      inventory: {
        revision: this.inventory.snapshot().playerInventory?.revision ?? -1,
        massGrams: this.inventory.snapshot().playerInventory?.massGrams ?? 0,
        slotCount: this.inventory.snapshot().playerInventory?.slotCount ?? 0,
        usedSlots: this.inventory.snapshot().playerInventory?.slots.filter(Boolean).length ?? 0,
        containerRevision: this.inventory.snapshot().container?.state.revision ?? -1,
        containerSessionId: this.inventory.snapshot().container?.accessSessionId ?? null,
        pendingRequests: this.inventory.snapshot().pendingRequests.size,
        lastResult: this.inventory.snapshot().lastResult?.result ?? null,
      },
      worldDrops: this.worldDrops.snapshot(),
      npc: this.npcs.snapshot(),
      combat: { ...this.combat.snapshot(this.camera, this.canvas), bytesReceived: this.network.combatBytesReceived },
      condition: this.condition.snapshot,
      drone: { ...this.drones.snapshot(this.camera, this.canvas), selectedId: this.dronePanel.selectedId },
      meshBytes: this.renderer.meshMemoryBytes,
      dev: {
        freeCam: this.freeCamEnabled,
        freeCamTarget: { x: this.freeCamTarget[0], y: this.freeCamTarget[1], z: this.freeCamTarget[2] },
        inputVertical: this.input.verticalAxis,
        render: { ...this.developerRenderFlags },
      },
    };
  }

  private frame(now: number): void {
    this.animationFrame = requestAnimationFrame((time) => this.frame(time));
    if (this.contextLost) return;
    const targetFps = this.settings.frameRate === "30" ? 30 : 60;
    if (now - this.previousFrame < 1_000 / targetFps - 1) return;
    const elapsed = Math.max(0.001, (now - this.previousFrame) / 1_000);
    const delta = Math.min(0.05, elapsed);
    this.previousFrame = now;
    // Consumers bound their own simulation work. The remote presentation clock
    // still needs the complete frame interval after a long render stall.
    this.update(delta, elapsed);
    this.render();
    // Graphics must react to real slow frames; the physics clamp would stretch
    // the five-second quality window to tens of seconds on an overloaded GPU.
    this.monitorAutomaticQuality(elapsed);
  }

  private update(delta: number, visualElapsed = delta): void {
    const previousPlayerX = this.playerX;
    const previousPlayerZ = this.playerZ;
    let discontinuousPosition = false;
    this.input.setDeveloperFreeFly(this.network.developer.freeFly);
    this.input.setGameplayDisabled((this.prediction.state.motionMode ?? PlayerMotionMode.Active) !== PlayerMotionMode.Active);
    this.input.update();
    const zoom = this.input.consumeZoom();
    this.camera.adjustZoom(
      zoom.multiplier,
      zoom.source === "touch" ? CAMERA_CONFIG.touchMinimumZoom : CAMERA_CONFIG.minimumZoom,
    );
    const forward = this.input.axisY;
    const right = this.input.axisX;
    const look = this.input.consumeCameraLook();
    if (this.camera.mode === "tps") {
      if (look.source) this.camera.orbit(look.dx, look.dy, look.source);
      const stick = this.input.consumeAimStick(visualElapsed);
      this.camera.rotateAim(stick.yaw, stick.pitch);
    }
    const movement = this.camera.mode === "tps"
      ? this.tpsMovement.resolve(right, forward, this.camera.groundBasis, this.camera.manualLookRevision)
      : undefined;
    // Legacy keeps fixed map-relative movement. TPS follows the current aim basis.
    const inputX = movement?.x ?? (-Math.sin(CAMERA_CONFIG.yaw) * forward + Math.cos(CAMERA_CONFIG.yaw) * right);
    const inputZ = movement?.z ?? (-Math.cos(CAMERA_CONFIG.yaw) * forward - Math.sin(CAMERA_CONFIG.yaw) * right);
    const vertical = this.input.verticalAxis;
    if (this.freeCamEnabled) {
      const speed = 4.2 * this.network.developer.speed * (this.input.running ? 1.48 : 1);
      this.freeCamTarget[0] += inputX * speed * delta;
      this.freeCamTarget[2] += inputZ * speed * delta;
      [this.freeCamTarget[0], this.freeCamTarget[2]] = clampWorldPosition(this.freeCamTarget[0], this.freeCamTarget[2]);
      this.freeCamTarget[1] += vertical * speed * delta;
    }

    if (this.input.uiBlocked || (this.prediction.state.motionMode ?? 0) !== PlayerMotionMode.Active) {
      this.pendingHandActions &= PlayerAction.MainRelease | PlayerAction.SecondaryRelease;
      this.commandSampler.cancel();
    }
    const hand = this.input.consumeHandActions();
    this.pendingHandActions |= (hand.rightPressed ? PlayerAction.MainPress : 0) | (hand.rightReleased ? PlayerAction.MainRelease : 0)
      | (hand.leftPressed ? PlayerAction.SecondaryPress : 0) | (hand.leftReleased ? PlayerAction.SecondaryRelease : 0);
    const authoritative = this.network.latest.self;
    if (authoritative) {
      this.authoritativeX = authoritative[1]; this.authoritativeY = authoritative[2]; this.authoritativeZ = authoritative[3];
      discontinuousPosition = this.prediction.reconcile(authoritative, this.network.latest.tick, this.network.latest.generation, this.predictionOptions());
      if (discontinuousPosition) this.commandSampler.cancel();
    }
    this.input.setGameplayDisabled((this.prediction.state.motionMode ?? PlayerMotionMode.Active) !== PlayerMotionMode.Active);
    if ((this.prediction.state.motionMode ?? 0) !== PlayerMotionMode.Active) {
      this.pendingHandActions &= PlayerAction.MainRelease | PlayerAction.SecondaryRelease;
      this.commandSampler.cancel();
    }
    const intent = (x: number, z: number, crouch: boolean, sprint: boolean): PlayerControlIntent => ({
      x: this.freeCamEnabled ? 0 : x, z: this.freeCamEnabled ? 0 : z,
      aimYaw: this.camera.mode === "tps" ? this.camera.aimYaw : Math.hypot(x, z) > .01 ? Math.atan2(x, z) : this.prediction.state.aimYaw ?? this.prediction.state.yaw,
      aimPitch: this.camera.mode === "tps" ? this.camera.aimPitch : 0,
      stance: crouch ? PlayerStance.Crouched : PlayerStance.Standing,
      gait: sprint && !crouch && (this.condition.snapshot?.modifiers.canSprint ?? true) ? PlayerGait.Sprint : PlayerGait.Walk,
      actions: this.pendingHandActions, vertical: this.freeCamEnabled ? 0 : vertical,
    });
    const sampledControl = (): PlayerControlIntent => {
      const sample = this.input.peekMovementSample();
      const sampled = this.camera.mode === "tps" ? this.tpsMovement.resolve(sample.x, sample.y, this.camera.groundBasis) : undefined;
      const x = sampled?.x ?? (-Math.sin(CAMERA_CONFIG.yaw) * sample.y + Math.cos(CAMERA_CONFIG.yaw) * sample.x);
      const z = sampled?.z ?? (-Math.cos(CAMERA_CONFIG.yaw) * sample.y - Math.sin(CAMERA_CONFIG.yaw) * sample.x);
      return this.commandSampler.sample(intent(x, z, sample.crouch, sample.sprint), () => { this.input.consumeMovementSample(); },
        Math.hypot(this.velocityX, this.velocityY, this.velocityZ) < .001);
    };
    // One frozen locomotion intent owns its complete 50 ms interval, including
    // every partial render preview. Mid-interval releases cannot rewind a pose.
    this.networkAccumulator = Math.min(.2, this.networkAccumulator + visualElapsed);
    while (this.networkAccumulator + 1e-8 >= PLAYER_COMMAND_SECONDS) {
      const command = this.network.sendControl(sampledControl());
      if (command) this.prediction.predict(command, this.predictionOptions());
      this.commandSampler.finish();
      this.pendingHandActions = 0;
      this.networkAccumulator = Math.max(0, this.networkAccumulator - PLAYER_COMMAND_SECONDS);
    }
    this.prediction.updateRender(visualElapsed);
    if (this.network.state !== "online") this.commandSampler.cancel();
    const preview = sampledControl();
    this.prediction.preview({ ...preview, run: preview.gait !== PlayerGait.Walk },
      this.network.state === "online" ? this.networkAccumulator : 0, this.predictionOptions());
    this.recordCollisionMetrics();

    const focusX = this.freeCamEnabled ? this.freeCamTarget[0] : this.playerX;
    const focusZ = this.freeCamEnabled ? this.freeCamTarget[2] : this.playerZ;
    this.terrain.update(focusX, focusZ, this.freeCamEnabled ? inputX : this.velocityX, this.freeCamEnabled ? inputZ : this.velocityZ);
    this.world.update(focusX, focusZ);
    const macroBiome = this.world.macroBiome;
    this.renderer.setBiomeAtmosphere(macroBiome.primary, macroBiome.secondary, macroBiome.secondaryWeight);

    this.character.ingestNetwork(this.network.latest, this.network.state);
    this.character.updateLocal(
      this.prediction.renderX,
      this.prediction.renderY,
      this.prediction.renderZ,
      discontinuousPosition ? 0 : this.velocityX,
      discontinuousPosition ? 0 : this.velocityZ,
      (this.prediction.state.gait ?? PlayerGait.Walk) !== PlayerGait.Walk,
      delta,
      this.prediction.state.yaw,
      this.prediction.state.locomotion, this.prediction.state.wetness,
      visualElapsed, Math.max(0, performance.now() - this.network.latest.receivedAt) / 1000,
      this.characterControl(),
    );

    const cameraStarted = performance.now();
    if (this.freeCamEnabled) {
      this.camera.updateFree(this.freeCamTarget[0], this.freeCamTarget[1], this.freeCamTarget[2], this.renderer.aspect);
    } else {
      if (discontinuousPosition && this.camera.mode === "tps") {
        this.camera.resetFollow();
        this.tpsMovement.reset();
      }
      if (this.camera.mode === "tps" && Math.hypot(this.playerX - this.cameraColliderX, this.playerZ - this.cameraColliderZ) > 1) {
        this.cameraColliders = getLargeWorldCollisionCandidatesAt(this.playerX, this.playerZ, TPS_CAMERA_CONFIG.maximumZoom + 2);
        this.cameraColliderX = this.playerX;
        this.cameraColliderZ = this.playerZ;
      }
      const stanceHeight = this.prediction.state.stance === PlayerStance.Crouched ? -.4 : 0;
      const motionHeight = this.prediction.state.motionMode === PlayerMotionMode.Ragdoll ? -.65 : 0;
      this.camera.update(this.prediction.renderX, this.waterCamera.targetY(this.prediction.renderY, this.prediction.state.locomotion, delta) + stanceHeight + motionHeight,
        this.prediction.renderZ, this.renderer.aspect, delta, this.prediction.state.yaw, undefined, visualElapsed);
    }

    this.cameraCpuMs = performance.now() - cameraStarted;
    const aimStarted = performance.now();
    this.aimQuery = this.camera.mode === "tps" ? queryGameplayAim(this.camera.position, this.camera.aimDirection, this.aimProbe) : undefined;
    this.character.setLocalAimPoint(this.aimQuery?.point, this.aimProbe);
    this.aimQueryCpuMs = performance.now() - aimStarted;

    this.renderer.setUnderwaterAmount(this.waterCamera.update(this.camera.position, delta));
    const waterUpdateStarted = performance.now();
    const waterTime = waterUpdateStarted / 1000;
    if (this.developerRenderFlags.water && this.developerRenderFlags.particles) {
      const water = sampleWorldWaterAt(this.playerX, this.playerZ);
      this.renderer.updateWaterActor(this.network.sessionId || "local", this.playerX, this.playerZ, water.surfaceY,
        this.velocityX, this.velocityZ, this.prediction.state.locomotion, waterTime);
      this.character.updateRemoteWaterEffects(waterTime, this.playerX, this.playerZ);
    }
    this.renderer.finishWaterEffects(waterTime, this.playerX, this.playerZ);
    this.renderer.metrics.waterUpdateCpuMs = performance.now() - waterUpdateStarted;

    const interactionOrigin = {
      x: this.playerX,
      y: this.playerY + 1.1,
      z: this.playerZ,
    };
    const interactionQueryStartedAt = performance.now();
    const interactionCandidates = this.interactionCandidateScratch;
    interactionCandidates.length = 0;
    if (!this.freeCamEnabled && (this.prediction.state.motionMode ?? 0) === PlayerMotionMode.Active && this.prediction.state.locomotion !== WaterLocomotion.Swimming) {
      const originTuple = [
        interactionOrigin.x,
        interactionOrigin.y,
        interactionOrigin.z,
      ] as const;
      interactionCandidates.push(...this.world.interactionCandidatesAt(originTuple));
      interactionCandidates.push(...this.worldDrops.interactionCandidatesAt(originTuple));
    }
    this.interaction.updateTargets(
      interactionCandidates,
      interactionOrigin,
      this.character.facingYaw,
      performance.now(),
      interactionQueryStartedAt,
    );
    if (this.input.consumeInteractionPress() && (this.prediction.state.motionMode ?? 0) === PlayerMotionMode.Active && this.prediction.state.locomotion !== WaterLocomotion.Swimming) {
      this.interaction.press(this.network.state === "online" ? this.network.sessionId : "");
    }
    if (this.input.consumeInteractionRelease()) this.interaction.release();
    this.interaction.updateActive(interactionOrigin, this.network.estimatedServerTick());
    this.onInteractionState?.(this.interaction.snapshot());
    this.npcs.update(delta, { x: this.playerX, y: this.playerY, z: this.playerZ });
    this.drones.update(this.network.estimatedServerNow());
    const combatWeather = sampleWeather(this.environmentState, this.environmentNow());
    this.combat.update(this.network.estimatedServerNow(), this.resolvedQuality, {
      precipitation: Math.max(this.environmentState.groundWetness, combatWeather.rainIntensity + combatWeather.hailIntensity),
      snowCover: Math.max(this.environmentState.snowCover, combatWeather.snowfallIntensity),
    });
    this.drones.selectedId = this.dronePanel.selectedId;
    this.dronePanel.update(this.drones.stateFor(this.dronePanel.selectedId), this.drones.telemetryFor(this.dronePanel.selectedId), this.playerX, this.playerY + 1.1, this.playerZ);
    this.drones.captionMaximumX = this.dronePanel.captionMaximumX;
  }

  private characterControl() {
    const state = this.prediction.state;
    return { bodyYaw: state.yaw, aimYaw: this.camera.mode === "tps" ? this.camera.aimYaw : state.aimYaw ?? state.yaw,
      aimPitch: this.camera.mode === "tps" ? this.camera.aimPitch : state.aimPitch ?? 0,
      stance: state.stance ?? PlayerStance.Standing, gait: state.gait ?? PlayerGait.Walk,
      motionMode: state.motionMode ?? PlayerMotionMode.Active, ragdollEvent: state.ragdollEvent ?? 0,
      ragdollStartTick: state.motionStartTick ?? 0, impulseX: state.impulseX ?? 0, impulseZ: state.impulseZ ?? 0,
      getUpVariant: state.getUpVariant ?? 0, motionEpoch: state.motionEpoch ?? 0,
      stateAgeSeconds: Math.max(0, (this.network.estimatedServerTick() - (state.motionStartTick ?? 0)) / 20) };
  }

  private recordCollisionMetrics(): void {
    this.collisionSamples += 1;
    this.collisionCandidateTotal += this.collisionProbe.candidateCount;
    this.renderer.metrics.collisionCandidates = this.collisionProbe.candidateCount;
    this.renderer.metrics.collisionCandidatesAverage = this.collisionCandidateTotal / this.collisionSamples;
    this.renderer.metrics.collisionCandidatesMax = Math.max(
      this.renderer.metrics.collisionCandidatesMax,
      this.collisionProbe.candidateCount,
    );
  }

  private predictionOptions(): PlayerLocomotionOptions {
    return { freeFly: this.network.developer.freeFly, speedMultiplier: this.network.developer.speed,
      resolvePosition: (x, z) => {
        if (this.network.developer.noclip || this.network.developer.freeFly) {
          this.collisionProbe.candidateCount = 0; return clampWorldPosition(x, z);
        }
        return resolveLargeWorldCollision(x, z, this.collisionProbe);
      } };
  }

  environmentSample(): { state: WorldEnvironmentState; at: number } {
    return { state: this.environmentState, at: this.environmentNow() };
  }

  private environmentNow(): number {
    return this.environmentServerAt + Math.max(0, performance.now() - this.environmentReceivedAt);
  }

  private render(): void {
    this.combat.cameraImpulse(this.camera.position, this.combatImpulse);
    withRenderImpulse(this.camera, this.combatImpulse, () => this.renderScene());
  }

  private renderScene(): void {
    const environmentStarted = performance.now();
    const serverEpoch = this.environmentNow();
    this.environmentFlash = this.weatherEffects.lightning.update(serverEpoch);
    this.renderer.setEnvironmentPresentation(buildEnvironmentPresentation(this.environmentState, serverEpoch, this.environmentFlash));
    this.environmentUpdateCpuMs = performance.now() - environmentStarted;
    const shadowCamera = this.renderer.beginShadowPass(this.freeCamEnabled
      ? this.freeCamTarget : [this.playerX, this.playerY, this.playerZ]);
    if (this.developerRenderFlags.characters) this.character.prepare(this.camera, shadowCamera ?? undefined);
    const localLights = [...PUBLIC_WORLD_LIGHTS, ...(this.developerRenderFlags.characters ? this.character.collectHeldLights() : [])];
    if (this.developerRenderFlags.particles) this.combat.appendLocalLights(localLights);
    this.renderer.setLocalLights(localLights);
    if (shadowCamera) {
      // Prioritize actors before spending the bounded static caster budget.
      if (this.developerRenderFlags.characters) {
        this.character.renderShadow(shadowCamera);
        this.npcs.renderShadow(shadowCamera);
      }
      if (this.developerRenderFlags.ai) {
        this.combat.renderShadow(shadowCamera);
        this.drones.renderShadow(shadowCamera);
        this.ai.render(shadowCamera);
      }
      if (this.developerRenderFlags.human) this.human.render(shadowCamera);
      if (this.developerRenderFlags.vegetation) this.vegetation.render(shadowCamera);
      if (this.developerRenderFlags.geology) this.geology.render(shadowCamera);
      if (this.developerRenderFlags.terrain) this.terrain.render(this.renderer, shadowCamera, false);
      this.renderer.endShadowPass();
    }
    this.renderer.renderLocalShadows(this.freeCamEnabled ? this.freeCamTarget : [this.playerX, this.playerY, this.playerZ], performance.now() / 1000,
      (camera) => {
        if (this.developerRenderFlags.human) this.human.render(camera);
        if (this.developerRenderFlags.geology) this.geology.render(camera);
        if (this.developerRenderFlags.vegetation) this.vegetation.render(camera);
        if (this.developerRenderFlags.ai) this.ai.render(camera);
        if (this.developerRenderFlags.terrain) this.terrain.render(this.renderer, camera, false);
      },
      (camera) => {
        if (this.developerRenderFlags.characters) { this.character.renderShadow(camera); this.npcs.renderShadow(camera); }
        if (this.developerRenderFlags.ai) this.drones.renderShadow(camera);
        this.worldDrops.render(camera);
      });
    this.renderer.begin(this.camera.viewProjection, this.camera.position, this.camera.target);
    if (this.developerRenderFlags.terrain) this.terrain.render(this.renderer, this.camera, false);
    if (this.developerRenderFlags.geology) this.geology.render(this.camera);
    if (this.developerRenderFlags.vegetation) this.vegetation.render(this.camera);
    if (this.developerRenderFlags.human) this.human.render(this.camera);
    this.worldDrops.render(this.camera);
    if (this.developerRenderFlags.ai) this.combat.render(this.camera);
    if (this.developerRenderFlags.ai) { this.ai.render(this.camera); this.drones.render(this.camera); }
    else this.drones.hide();
    if (this.developerRenderFlags.characters) {
      this.character.render(this.camera);
      this.npcs.render(this.camera, this.canvas);
    } else this.npcs.hide();
    this.contactShadows.begin(this.camera, this.resolvedQuality);
    if (this.renderer.atmosphereFeatureFlags.lighting && this.developerRenderFlags.ai) this.combat.addContactShadows(this.contactShadows);
    if (this.renderer.atmosphereFeatureFlags.lighting && this.developerRenderFlags.characters) {
      this.character.addContactShadows(this.contactShadows);
      this.npcs.addContactShadows(this.contactShadows);
    }
    if (this.renderer.atmosphereFeatureFlags.lighting && this.developerRenderFlags.ai) this.drones.addContactShadows(this.contactShadows);
    this.contactShadows.render();
    if (this.developerRenderFlags.terrain && this.developerRenderFlags.water) this.terrain.renderWater(this.renderer, this.camera);
    if (this.developerRenderFlags.water && this.developerRenderFlags.particles) this.renderer.drawWaterEffects();
    const nickname = this.network.latest.self?.[22].name;
    this.nameplates.render(this.developerRenderFlags.characters ? [
      ...(nickname ? [{ id: this.network.sessionId, name: nickname, x: this.playerX, y: this.playerY, z: this.playerZ }] : []),
      ...this.character.nameplatePlayers(),
    ] : [], { x: this.playerX, z: this.playerZ }, this.camera.viewProjection, this.renderer.viewportWidth, this.renderer.viewportHeight);
    if (this.developerRenderFlags.ai) this.drones.renderEffects(this.camera, this.canvas);
    if (this.developerRenderFlags.ai && this.developerRenderFlags.particles) this.combat.renderEffects(this.camera);
    if (this.developerRenderFlags.particles) this.renderer.drawAtmosphereParticles(
      this.camera.viewProjection,
      this.camera.position,
      this.camera.target,
      performance.now() / 1_000,
    );
    const weather = sampleWeather(this.environmentState, serverEpoch);
    this.weatherEffects.draw(this.camera, {
      rain: weather.rainIntensity, snow: weather.snowfallIntensity, hail: weather.hailIntensity,
      windSpeed: weather.windSpeed, windDirectionRadians: weather.windDirectionRadians,
      daylight: solarLighting(sampleWorldTime(this.environmentState, serverEpoch), sampleWorldCalendar(this.environmentState, serverEpoch).dayOfYear).intensity + this.environmentFlash,
    }, this.resolvedQuality, serverEpoch, this.terrain.getHeightAt(this.camera.position[0], this.camera.position[2]),
    this.developerRenderFlags.particles && this.renderer.atmosphereFeatureFlags.particles && this.waterCamera.underwaterAmount < 0.5);
    this.renderer.metrics.drawCalls += this.weatherEffects.metrics.precipitationDrawCalls + this.weatherEffects.metrics.lightningDrawCalls;
    this.renderer.metrics.triangles += this.weatherEffects.metrics.precipitationCount * 2 + this.weatherEffects.metrics.lightningDrawCalls * 72;
    this.renderer.end();
  }

  private monitorAutomaticQuality(delta: number): void {
    if (this.settings.graphics !== "auto") return;
    this.renderedFrames += 1;
    this.performanceWindow += delta;
    if (this.performanceWindow < 5) return;
    const measured = this.renderedFrames / this.performanceWindow;
    if (measured < 43 && this.quality !== presets.low) {
      this.resolvedQuality = "low";
      this.quality = presets.low;
      this.renderer.setQuality(this.quality.scale, this.quality.maximumWidth, "low");
    }
    this.renderedFrames = 0;
    this.performanceWindow = 0;
  }
}

function chooseAutomaticPreset(): Exclude<GraphicsQuality, "auto"> {
  const shortSide = Math.min(screen.width, screen.height);
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  if (memory <= 3 || shortSide < 390) return "low";
  if (memory >= 6 && shortSide >= 430) return "high";
  return "medium";
}
